import { BillLine, BillPayment, CreditNote, DirectSale, Refund, SalesDocument, TaxClass } from '../types/billing'
import { fromN, lineTaxClass, toN } from './billingMoney'

/**
 * Sales and tax report engine. Everything is computed from saved records in
 * whole ngwee. Basis: accrual — invoices count on their invoice date, direct
 * sales on their receipt date, credit notes on their credit-note date. Drafts,
 * voids, quotations and orders are never sales. Payments against invoices are
 * collections only, so an invoice paid later is never counted twice.
 */

export type ReportFilters = { from: string; to: string; department: string; itemId: string; accountId: string; cashier: string }
export type ReportSource = { documents: SalesDocument[]; sales: DirectSale[]; creditNotes: CreditNote[]; refunds: Refund[]; payments: BillPayment[] }

export type SalesKind = 'Invoice' | 'Direct sale' | 'Credit note'
export type TxnLine = { department: string; itemId?: string; description: string; taxCode: string; taxName: string; taxRate: number; taxClass: TaxClass; quantity: number; gross: number; discount: number; net: number; tax: number; total: number }
export type SalesTxn = { kind: SalesKind; id: string; sourceId: string; number: string; date: string; account: string; accountId?: string; cashier: string; lines: TxnLine[]; gross: number; discount: number; net: number; tax: number; total: number }

export type Totals = { count: number; gross: number; discount: number; net: number; tax: number; total: number }
const zero = (): Totals => ({ count: 0, gross: 0, discount: 0, net: 0, tax: 0, total: 0 })

const toTxnLine = (l: BillLine, factor = 1): TxnLine => {
  const total = Math.round(toN(l.total) * factor)
  const tax = Math.round(toN(l.tax) * factor)
  return {
    department: l.department || 'Unassigned', itemId: l.itemId, description: l.description, taxCode: l.taxCode ?? l.taxId.toUpperCase(), taxName: l.taxName,
    taxRate: l.taxRate, taxClass: lineTaxClass(l), quantity: l.quantity * factor,
    gross: Math.round(toN(l.gross) * factor), discount: Math.round(toN(l.discount) * factor), net: total - tax, tax, total,
  }
}

const sumLines = (lines: TxnLine[]) => lines.reduce((a, l) => ({ gross: a.gross + l.gross, discount: a.discount + l.discount, net: a.net + l.net, tax: a.tax + l.tax, total: a.total + l.total }), { gross: 0, discount: 0, net: 0, tax: 0, total: 0 })

/** Splits a credit note across its invoice's lines in proportion to line totals (to the ngwee). */
function creditLines(note: CreditNote, invoice?: SalesDocument): TxnLine[] {
  const amountN = toN(note.amount)
  if (!invoice || toN(invoice.total) <= 0) {
    return [{ department: 'Unassigned', description: `Credit — ${note.reason}`, taxCode: 'N/A', taxName: 'Not linked to an invoice', taxRate: 0, taxClass: 'Exempt', quantity: 1, gross: amountN, discount: 0, net: amountN, tax: 0, total: amountN }]
  }
  const totalN = toN(invoice.total)
  const lines = invoice.lines.map((l) => toTxnLine(l, amountN / totalN))
  const drift = amountN - lines.reduce((s, l) => s + l.total, 0)
  if (lines.length && drift) {
    const last = lines[lines.length - 1]
    last.total += drift
    last.net += drift
    last.gross += drift
  }
  return lines
}

/** All recognised sales transactions in the period (line-filtered by department / item). */
export function salesTransactions(src: ReportSource, f: ReportFilters): SalesTxn[] {
  const inRange = (d: string) => d >= f.from && d <= f.to
  const keepLine = (l: TxnLine) => (!f.department || l.department === f.department) && (!f.itemId || l.itemId === f.itemId)
  const keepTxn = (accountId: string | undefined, cashier: string) => (!f.accountId || accountId === f.accountId) && (!f.cashier || cashier === f.cashier)
  const out: SalesTxn[] = []
  const push = (t: Omit<SalesTxn, 'gross' | 'discount' | 'net' | 'tax' | 'total'>) => {
    const lines = t.lines.filter(keepLine)
    if (!lines.length || !keepTxn(t.accountId, t.cashier)) return
    out.push({ ...t, lines, ...sumLines(lines) })
  }
  for (const d of src.documents) {
    if (d.type !== 'invoice' || d.state !== 'Issued' || !inRange(d.date)) continue
    push({ kind: d.legacy?.source === 'sales-receipt' ? 'Direct sale' : 'Invoice', id: d.id, sourceId: d.id, number: d.number ?? '—', date: d.date, account: d.accountName, accountId: d.accountId, cashier: d.issuedByName ?? d.staffName, lines: d.lines.map((l) => toTxnLine(l)) })
  }
  for (const s of src.sales) {
    if (s.state !== 'Completed' || !inRange(s.date)) continue
    push({ kind: 'Direct sale', id: s.id, sourceId: s.id, number: s.number, date: s.date, account: s.accountName, accountId: s.accountId, cashier: s.cashierName, lines: s.lines.map((l) => toTxnLine(l)) })
  }
  for (const c of src.creditNotes) {
    if (c.state !== 'Issued' || !inRange(c.date)) continue
    const inv = c.invoiceId ? src.documents.find((d) => d.id === c.invoiceId) : undefined
    push({ kind: 'Credit note', id: c.id, sourceId: c.invoiceId ?? c.id, number: c.number, date: c.date, account: c.accountName, accountId: c.accountId, cashier: c.createdByName, lines: creditLines(c, inv) })
  }
  return out.sort((a, b) => a.date.localeCompare(b.date) || a.number.localeCompare(b.number))
}

export type SalesSummary = {
  invoice: Totals
  direct: Totals
  credits: Totals
  net: Totals
  departments: { name: string; net: number; tax: number; total: number }[]
  items: { name: string; quantity: number; net: number; total: number }[]
}

export function summariseSales(txns: SalesTxn[]): SalesSummary {
  const s = { invoice: zero(), direct: zero(), credits: zero() }
  const depts = new Map<string, { net: number; tax: number; total: number }>()
  const items = new Map<string, { quantity: number; net: number; total: number }>()
  for (const t of txns) {
    const bucket = t.kind === 'Invoice' ? s.invoice : t.kind === 'Direct sale' ? s.direct : s.credits
    bucket.count += 1
    bucket.gross += t.gross
    bucket.discount += t.discount
    bucket.net += t.net
    bucket.tax += t.tax
    bucket.total += t.total
    const sign = t.kind === 'Credit note' ? -1 : 1
    for (const l of t.lines) {
      const d = depts.get(l.department) ?? { net: 0, tax: 0, total: 0 }
      depts.set(l.department, { net: d.net + sign * l.net, tax: d.tax + sign * l.tax, total: d.total + sign * l.total })
      if (t.kind !== 'Credit note') {
        const it = items.get(l.description) ?? { quantity: 0, net: 0, total: 0 }
        items.set(l.description, { quantity: it.quantity + l.quantity, net: it.net + l.net, total: it.total + l.total })
      }
    }
  }
  const k = (t: Totals): Totals => ({ count: t.count, gross: fromN(t.gross), discount: fromN(t.discount), net: fromN(t.net), tax: fromN(t.tax), total: fromN(t.total) })
  const net: Totals = {
    count: s.invoice.count + s.direct.count,
    gross: s.invoice.gross + s.direct.gross,
    discount: s.invoice.discount + s.direct.discount,
    net: s.invoice.net + s.direct.net - s.credits.net,
    tax: s.invoice.tax + s.direct.tax - s.credits.tax,
    total: s.invoice.total + s.direct.total - s.credits.total,
  }
  return {
    invoice: k(s.invoice), direct: k(s.direct), credits: k(s.credits), net: k(net),
    departments: [...depts].map(([name, v]) => ({ name, net: fromN(v.net), tax: fromN(v.tax), total: fromN(v.total) })).sort((a, b) => b.total - a.total),
    items: [...items].map(([name, v]) => ({ name, quantity: Math.round(v.quantity * 100) / 100, net: fromN(v.net), total: fromN(v.total) })).sort((a, b) => b.total - a.total),
  }
}

export type CollectionRow = { kind: 'Invoice payment' | 'Deposit' | 'Direct sale' | 'Refund'; id: string; number: string; date: string; account: string; cashier: string; method: string; amount: number }

/** Money received (and refunded) in the period — kept separate from sales. */
export function collections(src: ReportSource, f: Pick<ReportFilters, 'from' | 'to' | 'accountId' | 'cashier'>): { rows: CollectionRow[]; byMethod: { method: string; amount: number }[]; total: number } {
  const inRange = (d: string) => d >= f.from && d <= f.to
  const keep = (accountId: string | undefined, cashier: string) => (!f.accountId || accountId === f.accountId) && (!f.cashier || cashier === f.cashier)
  const rows: CollectionRow[] = []
  for (const p of src.payments) {
    if (p.state !== 'Posted' || !inRange(p.date) || !keep(p.accountId, p.receivedByName)) continue
    rows.push({ kind: p.kind === 'Deposit' ? 'Deposit' : 'Invoice payment', id: p.id, number: p.receiptNumber, date: p.date, account: p.accountName, cashier: p.receivedByName, method: p.methodName, amount: p.amount })
  }
  for (const s of src.sales) {
    if (s.state !== 'Completed' || !inRange(s.date) || !keep(s.accountId, s.cashierName)) continue
    s.tenders.forEach((t) => rows.push({ kind: 'Direct sale', id: s.id, number: s.number, date: s.date, account: s.accountName, cashier: s.cashierName, method: t.methodName, amount: t.amount }))
  }
  for (const r of src.refunds) {
    if (!inRange(r.date) || !keep(r.accountId, r.createdByName)) continue
    rows.push({ kind: 'Refund', id: r.id, number: r.number, date: r.date, account: r.accountName, cashier: r.createdByName, method: r.methodName, amount: -r.amount })
  }
  const methods = new Map<string, number>()
  rows.forEach((r) => methods.set(r.method, (methods.get(r.method) ?? 0) + toN(r.amount)))
  return {
    rows: rows.sort((a, b) => a.date.localeCompare(b.date)),
    byMethod: [...methods].map(([method, n]) => ({ method, amount: fromN(n) })).sort((a, b) => b.amount - a.amount),
    total: fromN(rows.reduce((s, r) => s + toN(r.amount), 0)),
  }
}

export type TaxGroup = { key: string; taxCode: string; taxName: string; taxRate: number; taxClass: TaxClass; taxable: number; tax: number; gross: number; count: number }

/** Monthly sales tax summary grouped by tax code and rate. Credits are listed separately; refunds carry no tax. */
export function taxSummary(txns: SalesTxn[]) {
  const group = (kinds: SalesKind[]) => {
    const map = new Map<string, TaxGroup & { ids: Set<string> }>()
    txns.filter((t) => kinds.includes(t.kind)).forEach((t) => t.lines.forEach((l) => {
      const key = `${l.taxCode}|${l.taxRate}|${l.taxClass}`
      const g = map.get(key) ?? { key, taxCode: l.taxCode, taxName: l.taxName, taxRate: l.taxRate, taxClass: l.taxClass, taxable: 0, tax: 0, gross: 0, count: 0, ids: new Set<string>() }
      g.taxable += l.net
      g.tax += l.tax
      g.gross += l.total
      g.ids.add(t.id)
      map.set(key, g)
    }))
    return [...map.values()].map(({ ids, ...g }) => ({ ...g, count: ids.size, taxable: fromN(g.taxable), tax: fromN(g.tax), gross: fromN(g.gross) })).sort((a, b) => b.taxRate - a.taxRate || a.taxCode.localeCompare(b.taxCode))
  }
  const sales = group(['Invoice', 'Direct sale'])
  const credits = group(['Credit note'])
  const byClass = (rows: TaxGroup[], cls: TaxClass) => rows.filter((r) => r.taxClass === cls).reduce((s, r) => ({ taxable: s.taxable + toN(r.taxable), tax: s.tax + toN(r.tax) }), { taxable: 0, tax: 0 })
  const classes = (['Standard', 'Zero-rated', 'Exempt'] as TaxClass[]).map((cls) => {
    const s = byClass(sales, cls)
    const c = byClass(credits, cls)
    return { cls, sales: fromN(s.taxable), salesTax: fromN(s.tax), credits: fromN(c.taxable), creditTax: fromN(c.tax), net: fromN(s.taxable - c.taxable), netTax: fromN(s.tax - c.tax) }
  })
  return { sales, credits, classes, netTax: fromN(classes.reduce((s, c) => s + toN(c.netTax), 0)) }
}
